/**
 * Orchestrator for the proposals review surface inside the Flow Designer.
 *
 * Responsibilities:
 *   - Fetch pending proposals for the current workflow.
 *   - Render a floating pill in the canvas area with a count badge.
 *   - Open a local tray listing pending proposals.
 *   - Open a full-screen Review Overlay for a selected proposal.
 *
 * Mounts as a single self-contained component inside FlowDesigner so we don't
 * have to thread proposal state through the many layers of canvas props.
 */

import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ProposalsPill, ProposalsTray } from './ProposalsPill';
import { usePendingProposals } from './useWorkflowProposals';

interface FlowDesignerProposalsProps {
  workflowId: string | null;
  /** Called when the user picks a proposal to review. */
  onReview: (proposalId: string) => void;
}

/**
 * Floating pill + tray. Picking a proposal hands control up via `onReview`;
 * the parent is responsible for mounting the review view in place of the
 * canvas.
 */
export function FlowDesignerProposals({
  workflowId,
  onReview,
}: FlowDesignerProposalsProps) {
  const [trayOpen, setTrayOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const proposalsQ = usePendingProposals(workflowId);
  const proposals = proposalsQ.data ?? [];
  const count = proposals.length;

  // Click-outside to close the tray.
  useEffect(() => {
    if (!trayOpen) return;
    const handler = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setTrayOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [trayOpen]);

  useEffect(() => {
    if (!trayOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setTrayOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [trayOpen]);

  if (!workflowId) return null;

  return (
    <>
      <div
        ref={rootRef}
        className="pointer-events-none absolute right-4 bottom-4 z-30"
      >
        <div className="pointer-events-auto relative flex flex-col-reverse items-end gap-2">
          <ProposalsPill
            count={count}
            isLoading={proposalsQ.isLoading}
            isOpen={trayOpen}
            onClick={() => setTrayOpen((v) => !v)}
          />
          <AnimatePresence>
            {trayOpen && count > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 6, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 6, scale: 0.98 }}
                transition={{ duration: 0.15 }}
                className="w-[360px] rounded-lg border border-border bg-popover p-2 text-popover-foreground shadow-xl"
              >
                <ProposalsTray
                  proposals={proposals}
                  onReview={(id) => {
                    onReview(id);
                    setTrayOpen(false);
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
}
